import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Mic, Square, Loader2, Search, Plus, User, X, Pencil, Trash2,
  Calendar, ChevronRight, ArrowRight, Clock, Volume2, ChevronDown, Check
} from "lucide-react";
import { format } from "date-fns";
import BottomNav from "./BottomNav";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeaders = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DIAGNOSIS_LABELS = {
  diabetes_primary: { label: "Diabeet Esmane", color: "bg-amber-100 text-amber-700 border-amber-200" },
  diabetes_followup: { label: "Diabeet Korduv", color: "bg-orange-100 text-orange-700 border-orange-200" },
  diabetes: { label: "Diabeet", color: "bg-amber-100 text-amber-700 border-amber-200" },
  thyroid_primary: { label: "Kilpnääre Esmane", color: "bg-violet-100 text-violet-700 border-violet-200" },
  thyroid_followup: { label: "Kilpnääre Korduv", color: "bg-purple-100 text-purple-700 border-purple-200" },
  thyroid_nodules: { label: "Sõlmed/Kasvaja", color: "bg-rose-100 text-rose-700 border-rose-200" },
  thyroid: { label: "Kilpnääre", color: "bg-violet-100 text-violet-700 border-violet-200" },
  other: { label: "Muu", color: "bg-slate-100 text-slate-600 border-slate-200" },
};

const DIAGNOSIS_OPTIONS = [
  { value: "diabetes_primary", label: "Diabeet Esmane" },
  { value: "diabetes_followup", label: "Diabeet Korduv" },
  { value: "thyroid_primary", label: "Kilpnääre Esmane" },
  { value: "thyroid_followup", label: "Kilpnääre Korduv" },
  { value: "thyroid_nodules", label: "Sõlmed/Kasvaja" },
  { value: "other", label: "Muu" },
];

const TEMPLATE_FIELDS = {
  diabetes_primary: ["pohjus_ja_kaebused","diabeedi_tuup_ja_kestus","hba1c_ja_trend","veresuhkru_andmed","huepogluekeemia","kodune_ravi_annused","kaal_kmi_vererohk","dieet_ja_eluviis","silmad_retinoskopia","neerufunktsioon_uacr","jalad_neuropaatia","kardiovaskulaarne","muud_seisundid","ravi_muudatused_pohjendus","suunamised","jaergmine_vastuvoett","muu_arutatu"],
  diabetes_followup: ["kaebused","hba1c_ja_veresuhkur","ravi_ja_annused","kaal_rr","tuesistused","eluviis","plaan","muu_arutatu"],
  diabetes: ["pohjus_ja_kaebused","diabeedi_tuup_ja_kestus","hba1c_ja_trend","veresuhkru_andmed","huepogluekeemia","kodune_ravi_annused","kaal_kmi_vererohk","dieet_ja_eluviis","silmad_retinoskopia","neerufunktsioon_uacr","jalad_neuropaatia","kardiovaskulaarne","muud_seisundid","ravi_muudatused_pohjendus","suunamised","jaergmine_vastuvoett","muu_arutatu"],
  thyroid_primary: ["pohjus_ja_kaebused","diagnoos_ja_kestus","laborianaluusid","ultraheli_leid","kodune_ravi_annused","varasemad_protseduurid","silmad_graves","kardiovaskulaarne","muud_seisundid","ravi_muudatused_pohjendus","suunamised_uuringud","jaergmine_vastuvoett","muu_arutatu"],
  thyroid_followup: ["kaebused","laborianaluusid_trend","ravi_ja_annused","ultraheli","kaal_rr","tuesistused_silmad","plaan","muu_arutatu"],
  thyroid_nodules: ["kaebused_suemptomid","solme_kirjeldus","biopsia_tulemus","laborianaluusid","operatsioon_ravi","tureoglobuliin_jaelgimine","levothyroxin_annustamine","piltdiagnostika","riskikategooria","plaan_ja_jaelgimine","muu_arutatu"],
  thyroid: ["pohjus_ja_kaebused","diagnoos_ja_kestus","laborianaluusid","ultraheli_leid","kodune_ravi_annused","varasemad_protseduurid","silmad_graves","kardiovaskulaarne","muud_seisundid","ravi_muudatused_pohjendus","suunamised_uuringud","jaergmine_vastuvoett","muu_arutatu"],
  other: ["kaebused","kodune_ravi","ravi_muudatused","jaergmine_vastuvoett","muu_arutatu"],
};

const FIELD_LABELS = {
  kaebused: "Kaebused", pohjus_ja_kaebused: "Põhjus & kaebused", diabeedi_tuup_ja_kestus: "Diabeedi tüüp & kestus",
  hba1c_ja_trend: "HbA1c & trend", veresuhkru_andmed: "Veresuhkru andmed", huepogluekeemia: "Hüpoglükeemia",
  kodune_ravi_annused: "Kodune ravi & annused", kaal_kmi_vererohk: "Kaal / KMI / RR", dieet_ja_eluviis: "Dieet & eluviis",
  silmad_retinoskopia: "Silmad & retinoskoopia", neerufunktsioon_uacr: "Neerufunktsioon & UACR",
  jalad_neuropaatia: "Jalad & neuropaatia", kardiovaskulaarne: "Kardiovaskulaarne", muud_seisundid: "Muud seisundid",
  ravi_muudatused_pohjendus: "Ravi muudatused", suunamised: "Suunamised", jaergmine_vastuvoett: "Järgmine vastuvõtt",
  muu_arutatu: "Muu arutatu", hba1c_ja_veresuhkur: "HbA1c & veresuhkur", ravi_ja_annused: "Ravi & annused",
  kaal_rr: "Kaal & RR", tuesistused: "Tüsistused", eluviis: "Eluviis", plaan: "Plaan",
  diagnoos_ja_kestus: "Diagnoos & kestus", laborianaluusid: "Laborianalüüsid", ultraheli_leid: "Ultraheli leid",
  varasemad_protseduurid: "Varasemad protseduurid", silmad_graves: "Silmad & Graves",
  suunamised_uuringud: "Suunamised & uuringud", laborianaluusid_trend: "Laborianalüüsid & trend",
  tuesistused_silmad: "Tüsistused & silmad", kaebused_suemptomid: "Kaebused & sümptomid",
  solme_kirjeldus: "Sõlme kirjeldus", biopsia_tulemus: "Biopsia tulemus", operatsioon_ravi: "Operatsioon & ravi",
  tureoglobuliin_jaelgimine: "Türeoglobuliin", levothyroxin_annustamine: "Levothyroxin",
  piltdiagnostika: "Piltdiagnostika", riskikategooria: "Riskikategooria", plaan_ja_jaelgimine: "Plaan & jälgimine",
  ultraheli: "Ultraheli", kodune_ravi: "Kodune ravi", ravi_muudatused: "Ravi muudatused",
};
const getLabel = (k) => FIELD_LABELS[k] || k.replace(/_/g, " ");

// ========== PATIENT MANAGEMENT PANEL ==========
function PatientPanel({ open, onClose, patients, setPatients, onSelect, selectedPatient }) {
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [creating, setCreating] = useState(false);
  const [newPatient, setNewPatient] = useState({
    eesnimi: "", perekonnanimi: "", patient_id: "", diagnosis_type: "diabetes_primary",
    preferred_language: "et-EE", phone: "", email: ""
  });

  const filtered = patients.filter(p => {
    const q = search.toLowerCase();
    return !q || `${p.eesnimi} ${p.perekonnanimi} ${p.patient_id}`.toLowerCase().includes(q);
  });

  const handleCreate = async () => {
    if (!newPatient.eesnimi || !newPatient.perekonnanimi || !newPatient.patient_id) {
      toast.error("Eesnimi, perekonnanimi ja isikukood on kohustuslikud"); return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/medivoice/patients`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(newPatient)
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Viga"); }
      const p = await res.json();
      setPatients(prev => [p, ...prev]);
      onSelect(p);
      setShowCreate(false);
      setNewPatient({ eesnimi: "", perekonnanimi: "", patient_id: "", diagnosis_type: "diabetes_primary", preferred_language: "et-EE", phone: "", email: "" });
      toast.success("Patsient loodud!");
    } catch (e) { toast.error(e.message); } finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Kustutada patsient ja kõik seansid?")) return;
    try {
      const res = await fetch(`${API}/medivoice/patients/${id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error();
      setPatients(prev => prev.filter(p => p.id !== id));
      if (selectedPatient?.id === id) onSelect(null);
      toast.success("Patsient kustutatud");
    } catch { toast.error("Kustutamine ebaõnnestus"); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditData({ eesnimi: p.eesnimi, perekonnanimi: p.perekonnanimi, patient_id: p.patient_id,
      diagnosis_type: p.diagnosis_type, preferred_language: p.preferred_language || "et-EE",
      phone: p.phone || "", email: p.email || "" });
  };

  const saveEdit = async () => {
    try {
      const res = await fetch(`${API}/medivoice/patients/${editingId}`, {
        method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(editData)
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setPatients(prev => prev.map(p => p.id === editingId ? updated : p));
      if (selectedPatient?.id === editingId) onSelect(updated);
      setEditingId(null);
      toast.success("Salvestatud!");
    } catch { toast.error("Salvestamine ebaõnnestus"); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="patient-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">Patsiendid</h2>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => { setShowCreate(!showCreate); setEditingId(null); }} data-testid="panel-add-patient-btn">
              <Plus className="w-4 h-4 mr-1" />Uus
            </Button>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="close-patient-panel"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
        </div>

        {showCreate && (
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50 space-y-3" data-testid="panel-create-form">
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Eesnimi *" value={newPatient.eesnimi} onChange={e => setNewPatient({ ...newPatient, eesnimi: e.target.value })} data-testid="patient-eesnimi-input" />
              <Input placeholder="Perekonnanimi *" value={newPatient.perekonnanimi} onChange={e => setNewPatient({ ...newPatient, perekonnanimi: e.target.value })} data-testid="patient-perekonnanimi-input" />
            </div>
            <Input placeholder="Isikukood *" value={newPatient.patient_id} onChange={e => setNewPatient({ ...newPatient, patient_id: e.target.value })} data-testid="patient-id-input" />
            <div className="grid grid-cols-2 gap-3">
              <select className="h-10 rounded-md border border-input px-3 text-sm" value={newPatient.diagnosis_type} onChange={e => setNewPatient({ ...newPatient, diagnosis_type: e.target.value })} data-testid="patient-diagnosis-select">
                {DIAGNOSIS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select className="h-10 rounded-md border border-input px-3 text-sm" value={newPatient.preferred_language} onChange={e => setNewPatient({ ...newPatient, preferred_language: e.target.value })} data-testid="patient-lang-select">
                <option value="et-EE">Eesti</option><option value="ru-RU">Vene</option><option value="en-US">Inglise</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Telefon" value={newPatient.phone} onChange={e => setNewPatient({ ...newPatient, phone: e.target.value })} data-testid="patient-phone-input" />
              <Input placeholder="E-post" value={newPatient.email} onChange={e => setNewPatient({ ...newPatient, email: e.target.value })} data-testid="patient-email-input" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate} disabled={creating} className="flex-1 bg-[#0F52BA] hover:bg-[#0F52BA]/90" data-testid="create-patient-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Loo patsient
              </Button>
              <Button variant="outline" onClick={() => setShowCreate(false)} data-testid="cancel-patient-btn">Tühista</Button>
            </div>
          </div>
        )}

        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Otsi patsienti..." className="pl-10" value={search} onChange={e => setSearch(e.target.value)} data-testid="panel-search-input" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-2">
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">{patients.length === 0 ? "Ühtegi patsienti pole lisatud" : "Ei leitud"}</p>
          ) : filtered.map(p => (
            <div key={p.id} className="group" data-testid={`panel-patient-${p.id}`}>
              {editingId === p.id ? (
                <div className="py-3 space-y-2 border-b border-slate-50" data-testid={`edit-form-${p.id}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={editData.eesnimi} onChange={e => setEditData({ ...editData, eesnimi: e.target.value })} placeholder="Eesnimi" data-testid="edit-eesnimi" />
                    <Input value={editData.perekonnanimi} onChange={e => setEditData({ ...editData, perekonnanimi: e.target.value })} placeholder="Perekonnanimi" data-testid="edit-perekonnanimi" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <select className="h-9 rounded-md border border-input px-2 text-sm" value={editData.diagnosis_type} onChange={e => setEditData({ ...editData, diagnosis_type: e.target.value })} data-testid="edit-diagnosis">
                      {DIAGNOSIS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <select className="h-9 rounded-md border border-input px-2 text-sm" value={editData.preferred_language} onChange={e => setEditData({ ...editData, preferred_language: e.target.value })} data-testid="edit-language">
                      <option value="et-EE">Eesti</option><option value="ru-RU">Vene</option><option value="en-US">Inglise</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={editData.phone} onChange={e => setEditData({ ...editData, phone: e.target.value })} placeholder="Telefon" data-testid="edit-phone" />
                    <Input value={editData.email} onChange={e => setEditData({ ...editData, email: e.target.value })} placeholder="E-post" data-testid="edit-email" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit} className="bg-[#0F52BA] hover:bg-[#0F52BA]/90" data-testid="save-edit-btn"><Check className="w-3.5 h-3.5 mr-1" />Salvesta</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)} data-testid="cancel-edit-btn">Tühista</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 py-3 border-b border-slate-50">
                  <button className="flex-1 flex items-center gap-3 text-left hover:bg-slate-50 rounded-lg px-2 py-1 -mx-2 transition-colors"
                    onClick={() => { onSelect(p); onClose(); }} data-testid={`select-patient-${p.id}`}>
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <span className="text-sm font-semibold text-slate-600">{(p.eesnimi || "?")[0]}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{p.eesnimi} {p.perekonnanimi}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-slate-400 font-mono">{p.patient_id}</p>
                        {p.phone && <p className="text-xs text-slate-400">{p.phone}</p>}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${DIAGNOSIS_LABELS[p.diagnosis_type]?.color || DIAGNOSIS_LABELS.other.color}`}>
                      {DIAGNOSIS_LABELS[p.diagnosis_type]?.label || "Muu"}
                    </span>
                  </button>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button onClick={() => startEdit(p)} className="p-1.5 hover:bg-slate-100 rounded-md" title="Muuda" data-testid={`edit-patient-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="p-1.5 hover:bg-red-50 rounded-md" title="Kustuta" data-testid={`delete-patient-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ========== SIMPLE PATIENT DROPDOWN ==========
function PatientDropdown({ patients, selected, onSelect, onOpenPanel }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = patients.filter(p => {
    const q = search.toLowerCase();
    return !q || `${p.eesnimi} ${p.perekonnanimi} ${p.patient_id}`.toLowerCase().includes(q);
  });

  return (
    <div className="relative" ref={ref} data-testid="patient-dropdown">
      <button onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors text-left ${
          selected ? "bg-[#0F52BA]/5 border-[#0F52BA]/20" : "bg-white border-slate-200 hover:border-slate-300"
        }`} data-testid="patient-dropdown-toggle">
        {selected ? (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#0F52BA]/10 flex items-center justify-center">
              <span className="text-sm font-semibold text-[#0F52BA]">{(selected.eesnimi || "?")[0]}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{selected.eesnimi} {selected.perekonnanimi}</p>
              <p className="text-xs text-slate-400">{selected.patient_id} · {DIAGNOSIS_LABELS[selected.diagnosis_type]?.label || "Muu"}</p>
            </div>
          </div>
        ) : (
          <span className="text-sm text-slate-400">Vali patsient...</span>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-20 overflow-hidden" data-testid="patient-dropdown-list">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input placeholder="Otsi..." className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-[#0F52BA]/30"
                value={search} onChange={e => setSearch(e.target.value)} data-testid="dropdown-search" autoFocus />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">Ei leitud</p>
            ) : filtered.map(p => (
              <button key={p.id} onClick={() => { onSelect(p); setOpen(false); setSearch(""); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                  selected?.id === p.id ? "bg-[#0F52BA]/5" : "hover:bg-slate-50"
                }`} data-testid={`dropdown-patient-${p.id}`}>
                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <span className="text-xs font-semibold text-slate-600">{(p.eesnimi || "?")[0]}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.eesnimi} {p.perekonnanimi}</p>
                  <p className="text-xs text-slate-400 font-mono">{p.patient_id}</p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${DIAGNOSIS_LABELS[p.diagnosis_type]?.color || DIAGNOSIS_LABELS.other.color}`}>
                  {DIAGNOSIS_LABELS[p.diagnosis_type]?.label || "Muu"}
                </span>
              </button>
            ))}
          </div>
          <button onClick={() => { setOpen(false); onOpenPanel(); }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm text-[#0F52BA] hover:bg-slate-50 border-t border-slate-100 font-medium"
            data-testid="dropdown-manage-patients">
            <User className="w-4 h-4" />Halda patsiendid
          </button>
        </div>
      )}
    </div>
  );
}

// ========== RECORDING CONTROLS ==========
function RecordingControls({ onTranscription, onAudioId, selectedPatient }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const langMap = { "et-EE": "et", "ru-RU": "ru", "en-US": "en" };
  const language = langMap[selectedPatient?.preferred_language] || "et";

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.start(1000);
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch {
      toast.error("Mikrofoni ligipääs keelatud.");
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current) return;
    return new Promise((resolve) => {
      mediaRecorderRef.current.onstop = async () => {
        clearInterval(timerRef.current);
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) { toast.error("Salvestus liiga lühike"); resolve(); return; }
        const formData = new FormData();
        formData.append("file", blob, "recording.webm");
        try {
          const uploadRes = await fetch(`${API}/medivoice/audio/upload`, { method: "POST", headers: authHeaders(), body: formData });
          if (uploadRes.ok) { const data = await uploadRes.json(); onAudioId(data.file_id); }
        } catch { /* optional */ }
        setTranscribing(true);
        const transcribeForm = new FormData();
        transcribeForm.append("file", blob, "recording.webm");
        transcribeForm.append("language", language);
        try {
          const res = await fetch(`${API}/medivoice/transcribe`, { method: "POST", headers: authHeaders(), body: transcribeForm });
          if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Viga"); }
          const data = await res.json();
          onTranscription(data.transcription, duration);
          toast.success("Transkriptsioon valmis!");
        } catch (e) { toast.error(e.message || "Transkriptsioon ebaõnnestus"); }
        finally { setTranscribing(false); }
        resolve();
      };
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());
    });
  }, [language, duration, onTranscription, onAudioId]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  return (
    <div data-testid="recording-controls">
      {recording && (
        <div className="flex items-center gap-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
          <span className="text-sm font-mono font-medium text-red-600">{formatTime(duration)}</span>
        </div>
      )}
      {transcribing ? (
        <div className="flex items-center gap-3 py-4 justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#0F52BA]" />
          <span className="text-sm text-slate-600">Transkribeerin...</span>
        </div>
      ) : recording ? (
        <Button onClick={stopRecording} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="stop-recording-btn">
          <Square className="w-4 h-4 mr-2" />Peata salvestus
        </Button>
      ) : (
        <Button onClick={startRecording} disabled={!selectedPatient}
          className="w-full bg-[#0F52BA] hover:bg-[#0F52BA]/90 text-white" data-testid="start-recording-btn">
          <Mic className="w-4 h-4 mr-2" />Alusta salvestust
        </Button>
      )}
      {!selectedPatient && !recording && (
        <p className="text-xs text-amber-500 mt-2 text-center">Vali esmalt patsient</p>
      )}
    </div>
  );
}

// ========== TRANSCRIPTION PANEL ==========
function TranscriptionPanel({ transcription, onTranscriptionChange, selectedPatient }) {
  const [formatting, setFormatting] = useState(false);

  const formatTranscript = async () => {
    if (!transcription || transcription.length < 10) return;
    setFormatting(true);
    try {
      const res = await fetch(`${API}/medivoice/sessions/format-transcript`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ text: transcription })
      });
      if (!res.ok) throw new Error("Vormindamine ebaõnnestus");
      const data = await res.json();
      onTranscriptionChange(data.formatted_text);
      toast.success("Vormindatud!");
    } catch (e) { toast.error(e.message); } finally { setFormatting(false); }
  };

  const templateFields = selectedPatient ? TEMPLATE_FIELDS[selectedPatient.diagnosis_type] : null;
  const diagLabel = selectedPatient ? (DIAGNOSIS_LABELS[selectedPatient.diagnosis_type]?.label || "Muu") : null;

  return (
    <Card className="border-slate-200" data-testid="transcription-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Transkriptsioon</CardTitle>
          {transcription && (
            <Button variant="outline" size="sm" onClick={formatTranscript} disabled={formatting}
              className="text-xs" data-testid="format-transcript-btn">
              {formatting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Vorminda
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {templateFields && (
          <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid="template-guide">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{diagLabel} — malli punktid</p>
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              {templateFields.map((f, i) => (
                <li key={f} className="text-sm text-slate-600 flex items-start gap-1.5">
                  <span className="text-xs font-mono text-slate-400 mt-0.5 shrink-0">{i + 1}.</span>
                  {getLabel(f)}
                </li>
              ))}
            </ol>
          </div>
        )}
        <textarea
          className="w-full min-h-[200px] text-sm border border-slate-200 rounded-lg p-3 focus:ring-1 focus:ring-[#0F52BA]/20 focus:border-[#0F52BA] outline-none resize-y"
          placeholder="Transkriptsioon ilmub siia pärast salvestust. Võid ka käsitsi teksti sisestada."
          value={transcription}
          onChange={e => onTranscriptionChange(e.target.value)}
          data-testid="transcription-textarea"
        />
        {transcription && <p className="text-xs text-slate-400 mt-1">{transcription.length} tähemärki</p>}
      </CardContent>
    </Card>
  );
}

// ========== MAIN PAGE ==========
export default function MediVoicePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [transcription, setTranscription] = useState("");
  const [audioFileId, setAudioFileId] = useState(null);
  const [duration, setDuration] = useState(0);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({ session_count: 0, patient_count: 0, recent_sessions: [] });
  const [patientPanelOpen, setPatientPanelOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          fetch(`${API}/medivoice/patients`, { headers: authHeaders() }),
          fetch(`${API}/medivoice/stats`, { headers: authHeaders() })
        ]);
        if (pRes.ok) setPatients(await pRes.json());
        if (sRes.ok) setStats(await sRes.json());
      } catch { /* */ } finally { setLoadingPatients(false); }
    };
    fetchData();
  }, []);

  const handleTranscription = (text, dur) => {
    setTranscription(text);
    setDuration(dur || 0);
  };

  const saveSession = async () => {
    if (!selectedPatient || !transcription.trim()) {
      toast.error("Vali patsient ja sisesta transkriptsioon"); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/medivoice/sessions`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ patient_id: selectedPatient.id, transcription, duration_seconds: duration, language_detected: "Estonian" })
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Viga"); }
      const session = await res.json();
      if (audioFileId) {
        await fetch(`${API}/medivoice/sessions/${session.id}/audio?audio_file_id=${audioFileId}`, { method: "PUT", headers: authHeaders() });
      }
      toast.success("Seanss salvestatud!");
      navigate(`/medivoice/session/${session.id}`);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="medivoice-page">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="md:ml-64">
        <div className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
              <div>
                <h1 className="text-xl font-bold text-slate-900">MediVoice</h1>
                <p className="text-sm text-slate-500">Kliiniline vestluse salvestaja</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 max-w-6xl mx-auto">
          {/* Stats row */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <Card className="border-slate-200">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-2xl font-bold text-slate-900">{stats.session_count}</p>
                <p className="text-xs text-slate-500">Seanssi</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 cursor-pointer hover:border-[#0F52BA]/30 transition-colors"
              onClick={() => setPatientPanelOpen(true)} data-testid="patients-card">
              <CardContent className="pt-4 pb-3 px-4 flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.patient_count}</p>
                  <p className="text-xs text-slate-500">Patsienti</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300" />
              </CardContent>
            </Card>
          </div>

          {/* Patient selector + Recording */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="space-y-4">
              <PatientDropdown patients={patients} selected={selectedPatient} onSelect={setSelectedPatient}
                onOpenPanel={() => setPatientPanelOpen(true)} />
              {selectedPatient && (
                <Card className="border-slate-200">
                  <CardContent className="pt-4 pb-4 px-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Mic className="w-4 h-4" />Helisalvestus
                      <span className="text-xs font-normal text-slate-400 ml-auto">
                        {selectedPatient.preferred_language === "ru-RU" ? "Vene" : selectedPatient.preferred_language === "en-US" ? "Inglise" : "Eesti"}
                      </span>
                    </h3>
                    <RecordingControls onTranscription={handleTranscription} onAudioId={setAudioFileId} selectedPatient={selectedPatient} />
                  </CardContent>
                </Card>
              )}
            </div>
            <div className="lg:col-span-2 space-y-4">
              <TranscriptionPanel transcription={transcription} onTranscriptionChange={setTranscription} selectedPatient={selectedPatient} />
              {transcription && (
                <Button onClick={saveSession} disabled={saving || !selectedPatient}
                  className="w-full bg-[#0F52BA] hover:bg-[#0F52BA]/90 text-white h-12 text-base" data-testid="save-session-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  Salvesta ja loo kokkuvõte
                </Button>
              )}
            </div>
          </div>

          {/* Recent sessions */}
          {stats.recent_sessions?.length > 0 && (
            <div className="mt-8">
              <h2 className="text-base font-semibold text-slate-900 mb-3">Viimased seansid</h2>
              <div className="space-y-2">
                {stats.recent_sessions.slice(0, 5).map(s => (
                  <button key={s.id} onClick={() => navigate(`/medivoice/session/${s.id}`)}
                    className="w-full text-left bg-white border border-slate-200 rounded-lg px-4 py-3 hover:border-[#0F52BA]/30 transition-colors flex items-center gap-4"
                    data-testid={`recent-session-${s.id}`}>
                    <div className="w-9 h-9 rounded-full bg-[#0F52BA]/10 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-[#0F52BA]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{s.patient_name || "Tundmatu"}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {s.created_at ? format(new Date(s.created_at), "dd.MM.yyyy HH:mm") : ""}
                        </span>
                        {s.duration_seconds > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {Math.floor(s.duration_seconds / 60)}:{(s.duration_seconds % 60).toString().padStart(2, "0")}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Patient management panel */}
      <PatientPanel open={patientPanelOpen} onClose={() => setPatientPanelOpen(false)}
        patients={patients} setPatients={setPatients} onSelect={setSelectedPatient} selectedPatient={selectedPatient} />

      <BottomNav />
    </div>
  );
}
